import { useParams, Link, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { ArrowLeft, ChevronRight, CheckCircle } from "lucide-react";
import brands from "../data/brands.json";
import FAQ from "../components/FAQ";
import styles from "./ProductCategoryPage.module.css";

function generateSubProducts(category, brandName) {
  const models = ["Standard", "Plus", "Pro", "Max", "Elite"];

  return models.map((suffix, i) => ({
    id: `${category.id}-${i}`,
    name: `${category.name} ${suffix}`,
    image: category.image,
    description: `The ${category.name} ${suffix} from ${brandName} is engineered for demanding industrial environments.`,
    specs: [
      { label: "Capacity", value: `${(i + 1) * 15} – ${(i + 2) * 40} m³/h` },
      {
        label: "Pressure",
        value: `${(i + 1) * 2 + 4} – ${(i + 1) * 3 + 8} bar`,
      },
      { label: "Motor Power", value: `${(i + 1) * 5.5} kW` },
      { label: "Noise Level", value: `${60 + i * 2} dB(A)` },
      { label: "Weight", value: `${120 + i * 60} kg` },
    ],
    features: category.features || [],
  }));
}

export default function ProductCategoryPage() {
  const { brandSlug, categorySlug } = useParams();

  const brand = brands.find((b) => b.slug === brandSlug);
  const category = brand?.productCategories.find(
    (c) => c.slug === categorySlug,
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [brandSlug, categorySlug]);

  if (!brand || !category) return <Navigate to="/" replace />;

  const subProducts = generateSubProducts(category, brand.name);

  return (
    <>
      {/* HERO */}
      <section
        className={styles.hero}
        style={{ backgroundImage: `url(${category.image})` }}
      >
        <div
          className={styles.overlay}
          style={{
            background: `linear-gradient(120deg, ${brand.color}ee 0%, ${brand.color}99 60%, rgba(0,0,0,0.4) 100%)`,
          }}
        />
        <div className={styles.content}>
          <nav className={styles.breadcrumb}>
            <Link to="/" className={styles.breadLink}>
              Home
            </Link>
            <ChevronRight size={14} />
            <Link to={`/brands/${brand.slug}`} className={styles.breadLink}>
              {brand.name}
            </Link>
            <ChevronRight size={14} />
            <span>{category.name}</span>
          </nav>

          <p className={styles.brandName}>{brand.name}</p>
          <h1 className={styles.title}>{category.name}</h1>
          <p className={styles.brief}>{category.brief}</p>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="section" style={{ background: "var(--light-bg)" }}>
        <div className="container">
          <div className={styles.sectionHead}>
            <div>
              <p className="section-label">{brand.name}</p>
              <h2 className="section-title">{category.name} — Models</h2>
            </div>

            <Link to={`/brands/${brand.slug}`} className={styles.backBtn}>
              <ArrowLeft size={15} /> Back to {brand.name}
            </Link>
          </div>

          <div className={styles.productList}>
            {subProducts.map((product, i) => {
              const subject = `Request a Quote - ${product.name}`;

              return (
                <div key={product.id} className={styles.productItem}>
                  {/* IMAGE */}
                  <div className={styles.imgWrap}>
                    <img
                      src={product.image}
                      alt={product.name}
                      className={styles.img}
                    />
                    <div
                      className={styles.imgOverlay}
                      style={{ background: brand.color }}
                    />
                    <span className={styles.modelBadge}>Model {i + 1}</span>
                  </div>

                  {/* INFO */}
                  <div className={styles.info}>
                    <h3 className={styles.productName}>{product.name}</h3>

                    <p className={styles.productDesc}>{product.description}</p>

                    {/* SPECS */}
                    <div className={styles.specs}>
                      <h4 className={styles.specsTitle}>
                        Technical Specifications
                      </h4>
                      <table className={styles.specsTable}>
                        <tbody>
                          {product.specs.map((s) => (
                            <tr key={s.label}>
                              <td className={styles.specLabel}>{s.label}</td>
                              <td className={styles.specValue}>{s.value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* FEATURES */}
                    <div className={styles.features}>
                      {product.features.map((f, fi) => (
                        <span key={fi} className={styles.featureChip}>
                          <CheckCircle
                            size={12}
                            style={{ color: brand.color }}
                          />
                          {f}
                        </span>
                      ))}
                    </div>

                    {/* EMAIL BUTTON */}
                    <a
                      href={`https://mail.google.com/mail/?view=cm&fs=1&to=info@almotamakina.com&su=${encodeURIComponent(
                        subject,
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.inquireBtn}
                    >
                      Request a Quote
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <FAQ title={`${category.name} — FAQ`} />
    </>
  );
}
